import { getContentTypeByLabel } from "../../constants/contentType";

const ORDER_BY_COLUMN_MAP = {
  "Title.Exact": "Title",
  ReceivedAt: "ReceivedAt",
};

// SearchNavigation/ResultsHeader only ever produce 0 (ascending, "up" arrow) or 1 (descending,
// "down" arrow) internally - see ReorderButton.js. This mapping is our own frontend convention,
// not something the (old or new) backend dictates, so it's safe to fix here.
const ORDER_DIRECTION_MAP = {
  0: "Ascending",
  1: "Descending",
};

const KNOWN_ORIGINS = ["Globo", "Mercado"];

// Confirmed via GET dramaturgies/soapoperaschedules response: ids are sequential starting at 1,
// in the same order as SoapOperaScheduleDto's declaration (17h/18h/19h/21h).
const SOAP_OPERA_SCHEDULE_BY_ID = {
  1: "FivePM",
  2: "SixPM",
  3: "SevenPM",
  4: "NinePM",
  5: "ElevenPM",
};

// Confirmed via GET varieties/programfrequencies response: ids are sequential starting at 1,
// in the same order as ProgramFrequencyDto's declaration (Diário/Semanal).
const PROGRAM_FREQUENCY_BY_ID = {
  1: "Daily",
  2: "Weekly",
};

// Confirmed via GET projects/data/recommendations response: ids are sequential starting at 0
// (unlike SoapOperaSchedule/ProgramFrequency, which start at 1), in the same order as
// ProjectRecommendationTypeDto's declaration (Não Informado/Recomendado/Recomendado com
// ressalvas/Não recomendado).
const RECOMMENDATION_TYPE_BY_ID = {
  0: "Uninformed",
  1: "Recommended",
  2: "RecommendedWithCaveats",
  3: "NotRecommended",
};

const toArray = (value) => (value === undefined || value === null ? [] : [].concat(value));

const mapTypes = (types) => {
  const mapped = toArray(types).map(getContentTypeByLabel).filter(Boolean);
  return mapped.length ? mapped : undefined;
};

const mapOrigins = (origin) => (KNOWN_ORIGINS.includes(origin) ? [origin] : undefined);

const mapCreatorIds = (creators) => {
  const ids = toArray(creators)
    .map((creator) => creator?.CreatorId)
    .filter((id) => id !== undefined);
  return ids.length ? ids : undefined;
};

const mapProductionCompanyIds = (productionCompanies) => {
  const ids = toArray(productionCompanies)
    .map((company) => company?.value)
    .filter((id) => id !== undefined);
  return ids.length ? ids : undefined;
};

const mapComplementaryIds = (complementaries) => {
  const ids = toArray(complementaries);
  return ids.length ? ids : undefined;
};

const mapCharacterActivities = (activities) => {
  const names = toArray(activities)
    .map((activity) => (typeof activity === "string" ? activity : activity?.label))
    .filter(Boolean);
  return names.length ? names : undefined;
};

const toIsoDate = (date) => (date ? new Date(date).toISOString() : undefined);

const mapSoapOperaSchedule = (soapOperaSchedule) =>
  SOAP_OPERA_SCHEDULE_BY_ID[soapOperaSchedule?.value?.[0]];

const mapProgramFrequency = (programFrequency) =>
  PROGRAM_FREQUENCY_BY_ID[programFrequency?.value?.[0]];

const mapRecommendationTypes = (recommendationTypeIds) => {
  const mapped = toArray(recommendationTypeIds)
    .map((id) => RECOMMENDATION_TYPE_BY_ID[id])
    .filter(Boolean);
  return mapped.length ? mapped : undefined;
};

const mapOrderBy = (filter) => ORDER_BY_COLUMN_MAP[filter["OrderBy.Column"]];

const mapDirection = (filter) =>
  filter["OrderBy.Column"] ? ORDER_DIRECTION_MAP[filter["OrderBy.Direction"]] : undefined;

const omitUndefined = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== undefined));

// Translates the search screen's filter state (AppContext.filter) into the querystring shape
// expected by GET /api/v2/ContentSearch.
export const buildContentSearchParams = (filter = {}) =>
  omitUndefined({
    Term: filter.term || undefined,
    Title: filter.title || filter.Title,
    Types: mapTypes(filter.Types),
    FormatId: filter.FormatId,
    ComplementaryIds: mapComplementaryIds(filter.Complementaries),
    RecommendationTypes: mapRecommendationTypes(filter.recommendationTypeIds),
    CreatorIds: mapCreatorIds(filter.creators),
    Origins: mapOrigins(filter.origins),
    ProductionCompanyIds: mapProductionCompanyIds(filter.ProductionCompanies),
    ReceivedAtBegin: toIsoDate(filter.ReceivedAtBegin),
    ReceivedAtEnd: toIsoDate(filter.ReceivedAtEnd),
    CharacterName: filter.CharacterIdentifications,
    CharacterActivities: mapCharacterActivities(filter.characterActivities),
    SoapOperaSchedule: mapSoapOperaSchedule(filter.SoapOperaSchedule),
    ProgramFrequency: mapProgramFrequency(filter.ProgramFrequency),
    CreationModes: filter.CreationModes?.length ? filter.CreationModes : undefined,
    OrderBy: mapOrderBy(filter),
    Direction: mapDirection(filter),
    PageNumber: filter.PageNumber,
    PageSize: filter.PageSize,
  });
